










































/* General */
import Vue from 'vue'
import { debounce } from 'lodash'
import Clickoutside from 'element-ui/src/utils/clickoutside'

/* Mixins */

/* Components */

/* Helpers */
import TextAssistant from '@/helpers/text-assistant'

/* Data types */
import { IPersonWithEmail } from '@/api/types/response.types'

/* API */
import MailboxesAPI from '@/api/modules/mailboxes.api'

/* 
  This component is used for entering email addresses for conversations. It will load all existing
  customers from the passed-in mailbox ID so a user can select from the list (or type in their own)
*/
export default Vue.extend({
  name: 'ToEmailInput',

  directives: { Clickoutside },

  props: {
    mailboxId: {
      type: Number,
      required: true,
    },
    toEmail: {
      type: String,
      required: true,
    },
  },

  data() {
    return {
      isLoadingCustomers: false,
      shouldRefreshAvailableCustomers: true,

      availableCustomers: [] as IPersonWithEmail[],
      filteredAvailableCustomers: [] as IPersonWithEmail[],

      curToInputValue: '',
      debounceHandleToInputValue: null,
    }
  },

  computed: {
    internalToEmail: {
      get(): String {
        return this.toEmail
      },
      set(val: string) {
        if (!TextAssistant.isValidEmail(val)) {
          val = ''
          // @ts-ignore
          this.$refs.toEmailInputSelect.focus()
        }
        /* Can use sync or custom 'update-to-email' event to handle toEmail update */
        this.$emit('update:toEmail', val)
        this.$emit('update-to-email', val)
      },
    },
    internalToEmailAsArray: {
      get(): Array<String> {
        return this.toEmail.length > 0 ? [this.toEmail] : []
      },
      set(val: Array<string>) {
        this.internalToEmail = val.length > 0 ? val[0] : ''
      },
    },
    popperClass(): string {
      let result = 'options-dropdown '

      if (this.filteredAvailableCustomers.length === 0) {
        result += 'remove'
      }
      return result
    },
  },

  async created() {
    this.debounceHandleToInputValue = debounce(this.handleToInputValue, 250)
    await this.$nextTick()
    // @ts-ignore
    this.$refs.toEmailInputSelect.focus()
  },

  mounted() {
    this.addInputPlaceholders()

    // @ts-ignore
    this.$refs.toEmailInputSelect.$refs.tags.addEventListener(
      'click',
      (event: MouseEvent) => {
        event.stopPropagation()
      },
    )
  },

  methods: {
    getToLabel(customer: IPersonWithEmail): string {
      if (customer.name !== customer.email) {
        return customer.name + ' <' + customer.email + '>'
      }
      return customer.email
    },
    getOptionsHtmlLabel(customer: IPersonWithEmail): string {
      if (customer.name !== customer.email) {
        return (
          '<strong>' +
          customer.name +
          '</strong>&nbsp;&lt;' +
          customer.email +
          '&gt;'
        )
      }
      return customer.email
    },
    addInputPlaceholders() {
      const toInput: HTMLInputElement = this.$el.querySelector(
        '.el-select__input',
      )

      toInput.placeholder = 'Enter to address...'
    },

    async filterAvailableCustomers(val: string) {
      if (this.$screen.isMobile(this.$mq)) {
        return
      }
      if (val.trim().length <= 1) {
        this.filteredAvailableCustomers = []
        return
      }
      // Skip load/prune if we are still in the customer API call...
      if (!this.isLoadingCustomers) {
        if (this.shouldRefreshAvailableCustomers) {
          try {
            this.isLoadingCustomers = true
            this.availableCustomers = await MailboxesAPI.get_Customers(
              this.mailboxId,
            )
            this.isLoadingCustomers = false
            this.shouldRefreshAvailableCustomers = false
          } catch (error) {
            this.$error.emit(error)
          }
        }

        const loweredVal = val.toLowerCase()

        this.filteredAvailableCustomers = this.availableCustomers
          .filter(
            i =>
              i.email.includes(loweredVal) ||
              i.name.toLowerCase().includes(loweredVal),
          )
          .slice(0, 200)
      }
    },

    attemptToEmailPush(val: string) {
      if (TextAssistant.isValidEmail(val)) {
        this.internalToEmail = val
      }
      this.clearInputFields()
    },

    clearInputFields() {
      this.curToInputValue = ''
      // @ts-ignore
      this.$refs.toEmailInputSelect.query = ''
      this.filterAvailableCustomers('')
    },

    async handleToInputValue(val: string) {
      /* Store the current value separately. This will be used
      in case user focuses out of the select field so we can evaluate
      whether the last typed-in value needs to be processed. Otherwise,
      the 'this.$refs.toEmailInputSelect.query' is null whenever we
      lose focus*/
      this.curToInputValue = val
      if (val.trim().length < 1) {
        return
      }

      // Skip load/prune if we are still in the customer API call...
      await this.filterAvailableCustomers(val)

      // Treat comma as a delimiter
      if (val.endsWith(',')) {
        const inputNoComma = val.slice(0, -1)
        // Checking if includes comma because if you just hold "," sometimes
        // handleCurInputValue will pass a value of some number of commas because of debouncing
        if (inputNoComma !== '' && !inputNoComma.includes(',')) {
          this.attemptToEmailPush(inputNoComma)
        }
      }
    },

    handleToDelimiter() {
      // @ts-ignore
      const inputValue = this.$refs.toEmailInputSelect.query.trim()
      this.attemptToEmailPush(inputValue)
    },

    handleToVisibleChange(value: boolean) {
      /* If we are clicking out of a non-search only picker, 
         process any remaining value in the input. */
      if (!value) {
        this.attemptToEmailPush(this.curToInputValue)
      }
    },
  },
})
